import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { startOfDay, endOfDay, subDays, format } from "date-fns";

export type DatePreset = "today" | "yesterday" | "last7" | "custom";

export interface KdsPerformanceItem {
  id: string;
  nameSnapshot: string;
  quantity: number;
  openedAt: string;
  doneAt: string;
  durationSeconds: number;
  orderId: string;
  orderNumber: number | null;
  tableNumber: string | null;
  tabNumber: string | null;
  customerName: string | null;
  productionLocationId: string;
}

export interface KdsPerformanceSummary {
  avgSeconds: number;
  bestItem: KdsPerformanceItem | null;
  worstItem: KdsPerformanceItem | null;
  totalItems: number;
}

export function useKdsPerformanceReport() {
  const { activeRestaurant } = useRestaurantContext();
  const [datePreset, setDatePreset] = useState<DatePreset>("today");
  const [customRange, setCustomRange] = useState<{ from: Date; to: Date }>({
    from: new Date(),
    to: new Date(),
  });
  const [locationId, setLocationId] = useState<string>("all");
  const [items, setItems] = useState<KdsPerformanceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (datePreset) {
      case "today":
        return { from: startOfDay(now), to: endOfDay(now) };
      case "yesterday":
        return { from: startOfDay(subDays(now, 1)), to: endOfDay(subDays(now, 1)) };
      case "last7":
        return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
      case "custom":
        return { from: startOfDay(customRange.from), to: endOfDay(customRange.to) };
    }
  }, [datePreset, customRange]);

  useEffect(() => {
    if (!activeRestaurant?.id) return;

    const fetchData = async () => {
      setIsLoading(true);

      let query = supabase
        .from("kds_ticket_items")
        .select("id, name_snapshot, quantity, opened_at, done_at, order_id, production_location_id")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "done")
        .not("done_at", "is", null)
        .gte("done_at", dateRange.from.toISOString())
        .lte("done_at", dateRange.to.toISOString());

      if (locationId !== "all") {
        query = query.eq("production_location_id", locationId);
      }

      const { data: ticketItems, error } = await query;

      if (error || !ticketItems || ticketItems.length === 0) {
        setItems([]);
        setIsLoading(false);
        return;
      }

      const orderIds = [...new Set(ticketItems.map((t) => t.order_id))];

      const { data: orders } = await supabase
        .from("orders")
        .select("id, order_number, table_number, tab_number, customer_name")
        .in("id", orderIds);

      const ordersMap = new Map(orders?.map((o) => [o.id, o]) ?? []);

      const mapped: KdsPerformanceItem[] = ticketItems.map((t) => {
        const order = ordersMap.get(t.order_id);
        const durationSeconds =
          (new Date(t.done_at!).getTime() - new Date(t.opened_at).getTime()) / 1000;
        return {
          id: t.id,
          nameSnapshot: t.name_snapshot,
          quantity: t.quantity,
          openedAt: t.opened_at,
          doneAt: t.done_at!,
          durationSeconds: Math.max(0, durationSeconds),
          orderId: t.order_id,
          orderNumber: order?.order_number ?? null,
          tableNumber: order?.table_number ?? null,
          tabNumber: order?.tab_number ?? null,
          customerName: order?.customer_name ?? null,
          productionLocationId: t.production_location_id,
        };
      });

      mapped.sort((a, b) => new Date(b.doneAt).getTime() - new Date(a.doneAt).getTime());
      setItems(mapped);
      setIsLoading(false);
    };

    fetchData();
  }, [activeRestaurant?.id, dateRange, locationId]);

  const summary: KdsPerformanceSummary = useMemo(() => {
    if (items.length === 0) {
      return { avgSeconds: 0, bestItem: null, worstItem: null, totalItems: 0 };
    }
    const total = items.reduce((sum, i) => sum + i.durationSeconds, 0);
    const best = items.reduce((a, b) => (a.durationSeconds < b.durationSeconds ? a : b));
    const worst = items.reduce((a, b) => (a.durationSeconds > b.durationSeconds ? a : b));
    return {
      avgSeconds: total / items.length,
      bestItem: best,
      worstItem: worst,
      totalItems: items.length,
    };
  }, [items]);

  return {
    items,
    summary,
    isLoading,
    datePreset,
    setDatePreset,
    customRange,
    setCustomRange,
    locationId,
    setLocationId,
  };
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}
